/**
 * Letting the mind INTERPRET what it remembers, instead of scanning it.
 *
 * `pure/visualPrompt.ts` asks "which words here describe appearance?" and it is
 * very good at that. Measured across the five lattices:
 *
 *   printing    23.2% of facts carry a word it can see
 *   gardening   16.7%
 *   hermes       8.5%   and 11 distinct words, 63 of them "emerald"
 *   webdev       1.7%
 *   spark-kit    0.8%
 *
 * For a corpus about ideas that is close to nothing, and the reason is not that
 * the Hermetic material is unvisual. It is soaked in imagery: an emerald
 * tablet, a sealed vessel, as above so below. **The lexicon cannot see any of
 * it, because it is looking for adjectives and the imagery is in the nouns.**
 * "Emerald tablet" scores only because "emerald" happens to be a colour.
 *
 * So this asks the model instead. Not "find me the visual words" but "what
 * would a picture of this look like", which is a question only something that
 * understands the material can answer.
 *
 * WHY JANUS IS ALLOWED TO DO THIS, given it grounds at 25% against Gemma 3 1B's
 * 90%: that number measured FACTUAL CLAIM ACCURACY, and it disqualified Janus
 * as a chat tier for exactly the right reason. This is a different job. Here we
 * want an evocative phrase, and invention is the task rather than the failure
 * mode. The number is real and it is not the relevant one.
 *
 * WHAT WE GIVE UP, and it must not be given up silently: the lexicon path can
 * say precisely which remembered phrases it added. An interpretation cannot,
 * so `overlap` measures how much of the result actually traces back to the
 * facts, and the UI reports it. A picture that claims to come from a memory and
 * does not is exactly the ghost variable this whole bay is built to avoid.
 */

import type { MindClient } from "./llm/client";
import { cleanScene, sceneOverlap } from "./pure/visualPrompt";

export type Scene = {
  /** What to hand the image model. */
  prompt: string;
  /** Exactly what the model said, before cleaning. Shown so a bad read is legible. */
  raw: string;
  /**
   * Share of the prompt's content words that appear in the remembered facts,
   * 0 to 1. Low is not a failure; it is a fact about how freely it interpreted,
   * and the visitor is told.
   */
  overlap: number;
};

/**
 * Deliberately short, and it names the job rather than describing it.
 *
 * A 1B model given a long instruction spends its small attention budget on the
 * instruction. The facts are the payload and everything else is overhead, so
 * everything else is kept to one line.
 */
function ask(subject: string, facts: string[]): string {
  // Trim each fact: a memory banner is prose and the model needs the gist, not
  // the clause structure. Twelve facts of full length would bury the request.
  const lines = facts.slice(0, 8).map((f) => `- ${f.replace(/\s+/g, " ").trim().slice(0, 160)}`);
  return (
    `A mind remembers these things:\n${lines.join("\n")}\n\n` +
    `Someone is drawing "${subject}". Using what the mind remembers, describe what ` +
    `should be IN that picture: the setting, the objects, the light, the mood. ` +
    `One vivid phrase, under 25 words. Do not name the subject again. No explanation.`
  );
}

/**
 * Compose an image prompt by reading the facts rather than scanning them.
 *
 * Returns null when the model gives back nothing usable, so the caller can fall
 * back to the lexicon rather than draw an empty prompt. A model that returns
 * two words has failed, and "a" is not a picture.
 */
export async function interpretScene(
  llm: MindClient,
  subject: string,
  facts: string[],
  opts: { maxNewTokens?: number; temperature?: number } = {},
): Promise<Scene | null> {
  if (facts.length === 0) return null;

  const out = await llm.generate([{ role: "user", content: ask(subject, facts) }], {
    maxNewTokens: opts.maxNewTokens ?? 96,
    // NOT zero. The literal path is the deterministic one; this is the arm
    // whose entire purpose is to be interesting, and a greedy decode of a 1B
    // model on a creative instruction produces the flattest sentence it knows.
    temperature: opts.temperature ?? 0.9,
  });

  const detail = cleanScene(out.text ?? "");
  // Three words is the floor. Below that it has echoed the instruction or
  // produced a fragment, and the lexicon will do better.
  if (detail.split(/\s+/).filter(Boolean).length < 3) return null;

  /* THE SUBJECT COMES FIRST AND IS NEVER REWRITTEN, exactly as on the lexicon
   * path. The first version of this let the model compose the whole prompt,
   * and asked for "the emerald tablet" it produced a scroll among treasures
   * that never mentioned an emerald or a tablet: evocative, drawable, and not
   * the thing that was asked for. A memory that can overrule the request is a
   * memory that draws the wrong thing confidently, and that rule does not stop
   * applying because the mechanism got cleverer. */
  const prompt = `${subject.trim()}, ${detail}`;
  // `overlap` is scored on the DETAIL alone. Including the subject would credit
  // the interpretation for words the visitor supplied, which is the sort of
  // free marks that makes a number stop meaning anything.
  return { prompt, raw: (out.text ?? "").trim(), overlap: sceneOverlap(detail, facts) };
}
